import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { withRouter, Redirect } from 'react-router-dom';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import { reduxForm, getFormValues } from 'redux-form/immutable';
import { LinearProgress, CircularProgress } from '@material-ui/core';

import Box from 'retired/Box';
import { showErrorAction } from 'containers/Notification/actions';
import { PureRender } from 'utils/component-helpers';
import { useInjectReducer } from 'utils/injectReducer';
import { useInjectEpics } from 'utils/injectEpics';
import DonationMatchingRuleType from 'entities/donationMatchingRule/model';
import {
  selectOrganization,
  selectAccessibleUserLocations,
  selectUser,
} from 'entities/user/selectors';
import { selectBudgets } from 'entities/budget/selectors';
import { selectCategories } from 'entities/cause/selectors';
import { selectCampaigns } from 'entities/campaign/selectors';

import DonationMatchingRule from './DonationMatchingRule';
import * as Actions from './actions';
import { FORM_IDENTIFIER } from './constants';
import { selectLoading } from './selectors';
import epics from './epics';
import reducer from './reducer';

const key = 'donationMatchingRulesFormPage';

const DonationMatchingRulesFormPage = ({
  loading,
  pristine,
  invalid,
  submitting,
  actions,
  organization,
  handleSubmit,
  match,
  history,
  donationMatchingRule,
  budgets,
  categories,
  campaigns,
  locations,
  user,
}) => {
  useInjectReducer({ key, reducer });
  useInjectEpics({ key, epics });
  useEffect(() => {
    actions.initAction(match.params.id, match.params.budgetId);
  }, []);

  const submit = (formData) => {
    if (pristine) {
      history.replace('/donations/rules');
    } else {
      actions.saveAction(DonationMatchingRuleType.fromFormToRequestJson(formData.toJS()));
    }
  };

  if (organization && !organization.isCompany()) {
    return <Redirect to="/donations" replace />;
  }

  const title = 'Donation Matching Rule';

  const budget = budgets?.find(
    (b) => b.id === (donationMatchingRule?.budget ?? match.params.budgetId)
  );

  return (
    <>
      {loading && <LinearProgress />}
      {!loading && (
        <Box>
          <Helmet title={title} />
          <h1>{title}</h1>
          {submitting ? (
            <CircularProgress />
          ) : (
            <form onSubmit={handleSubmit(submit)}>
              <div>
                <DonationMatchingRule
                  donationMatchingRule={donationMatchingRule}
                  budget={budget}
                  categories={categories}
                  campaigns={campaigns}
                  locations={locations}
                  pristine={pristine}
                  invalid={invalid}
                  remove={() => actions.removeAction(match.params.id)}
                  donationMatchingRuleId={match.params.id}
                  organization={organization}
                  user={user}
                />
              </div>
            </form>
          )}
        </Box>
      )}
    </>
  );
};
DonationMatchingRulesFormPage.propTypes = {
  loading: PropTypes.bool,
  categories: PropTypes.object,
  campaigns: PropTypes.object,
  locations: PropTypes.object,
  pristine: PropTypes.bool,
  invalid: PropTypes.bool,
  submitting: PropTypes.bool,
  actions: PropTypes.object,
  match: PropTypes.object,
  history: PropTypes.object,
  donationMatchingRule: PropTypes.object,
  organization: PropTypes.object,
  budgets: PropTypes.object,
  user: PropTypes.object,
  handleSubmit: PropTypes.func,
};

const getFormValuesSelector = getFormValues(FORM_IDENTIFIER);

const withConnect = connect(
  (state) => ({
    donationMatchingRule: getFormValuesSelector(state),
    loading: selectLoading(state),
    organization: selectOrganization(state),
    categories: selectCategories(state),
    campaigns: selectCampaigns(state),
    budgets: selectBudgets(state),
    locations: selectAccessibleUserLocations(state),
    user: selectUser(state),
  }),
  (dispatch) => ({
    actions: bindActionCreators({ ...Actions, showErrorAction }, dispatch),
  })
);

const withForm = reduxForm({
  form: FORM_IDENTIFIER,
  destroyOnUnmount: false,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
});

export default compose(
  withRouter,
  withConnect,
  withForm,
  PureRender
)(DonationMatchingRulesFormPage);
